import { achatarFiltros, ORDENACOES, type FiltrosDinamicos } from './checkin-filtros';

/**
 * Estado filtrável da tela, na URL.
 *
 * Existe para o filtro ser COMPARTILHÁVEL: o produtor manda o link da sessão
 * de sábado para quem está na portaria e a pessoa abre exatamente a mesma
 * lista, em vez de receber instruções de que cliques dar.
 *
 * Regras que valem a pena não redescobrir:
 * - **valor padrão não vai para a URL.** `?sort=dataCheckin,desc` é ruído: é o
 *   que a tela já faz sem parâmetro nenhum. URL curta é URL que se cola no
 *   WhatsApp sem quebrar.
 * - **`page` é 1-based na URL**, 0-based no backend. Quem lê a URL é gente.
 * - **valor inválido cai no padrão**, sem erro: link velho ou editado à mão
 *   abre a tela, não uma tela quebrada.
 * - **filtros dinâmicos (GUE-391) não são campos nomeados aqui** — `filtros`
 *   é genérico por `path` do catálogo (`campo[operador]=valor` na URL, mesmo
 *   shape que a query do backend). Um filtro novo no catálogo (`/filtros`)
 *   não pede mudança nenhuma neste arquivo.
 */
export interface EstadoCheckin {
  filtro: string;
  sessaoId: string | null;
  ordem: string;
  /** 0-based, como o backend. */
  pagina: number;
  /** GUE-391: ver {@link FiltrosDinamicos}. */
  filtros: FiltrosDinamicos;
}

export const ESTADO_PADRAO: EstadoCheckin = {
  filtro: '',
  sessaoId: null,
  ordem: ORDENACOES[0].valor,
  pagina: 0,
  filtros: {},
};

type Fonte = { get(chave: string): string | null; keys: readonly string[] };

const PARAM_FILTRO_DINAMICO = /^(.+)\[(.+)]$/;

function lerFiltrosDinamicos(qp: Fonte): FiltrosDinamicos {
  const filtros: FiltrosDinamicos = {};
  for (const chave of qp.keys) {
    const casado = PARAM_FILTRO_DINAMICO.exec(chave);
    if (!casado) continue;
    const valor = qp.get(chave)?.trim();
    if (!valor) continue;
    const [, path, operador] = casado;
    filtros[path] = { ...filtros[path], [operador]: valor };
  }
  return filtros;
}

export function paramsParaEstado(qp: Fonte): EstadoCheckin {
  const ordem = qp.get('sort');
  const paginaCrua = Number(qp.get('page'));
  return {
    filtro: qp.get('filtro')?.trim() || '',
    sessaoId: qp.get('sessaoId')?.trim() || null,
    ordem: ORDENACOES.some((o) => o.valor === ordem) ? (ordem as string) : ESTADO_PADRAO.ordem,
    pagina: Number.isInteger(paginaCrua) && paginaCrua > 1 ? paginaCrua - 1 : 0,
    filtros: lerFiltrosDinamicos(qp),
  };
}

/**
 * `null` numa chave remove o parâmetro da URL (contrato do Router).
 *
 * `chavesFiltroConhecidas` (de {@link import('./checkin-filtros').chavesDeFiltro})
 * é o que permite ZERAR um filtro que deixou de estar ativo: sem saber as
 * chaves possíveis do catálogo atual, escrever só o que está em
 * `estado.filtros` nunca removeria uma chave que já estava na URL — o
 * `queryParamsHandling: 'merge'` do Router deixa intocado o que não é
 * mencionado. Omitir o argumento (ex. catálogo ainda não carregado) é seguro:
 * só significa que nada é zerado nesse ciclo.
 */
export function estadoParaParams(
  estado: EstadoCheckin,
  chavesFiltroConhecidas: readonly string[] = [],
): Record<string, string | null> {
  const achatado = achatarFiltros(estado.filtros);
  const dinamico: Record<string, string | null> = {};
  for (const chave of chavesFiltroConhecidas) dinamico[chave] = achatado[chave] ?? null;
  // Defensivo: uma chave ativa fora do catálogo conhecido (ex. corrida entre o
  // catálogo carregar e o filtro já vir da URL) ainda assim é escrita.
  for (const [chave, valor] of Object.entries(achatado)) dinamico[chave] ??= valor;

  return {
    filtro: estado.filtro.trim() || null,
    sessaoId: estado.sessaoId || null,
    sort: estado.ordem === ESTADO_PADRAO.ordem ? null : estado.ordem,
    page: estado.pagina > 0 ? String(estado.pagina + 1) : null,
    ...dinamico,
  };
}
